import React, { useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Breadcrumb from "../../Components/Navigation/Breadcrumbs/Breadcrumbs";
import { useLocation, Redirect } from "react-router-dom";
import { Button, Card, CardContent, Grid, TextField, TextFieldProps } from "@material-ui/core";
import { generateCsv, mkConfig, download } from "export-to-csv";
import { ClientBillingReportDisplay, ClientBillingReportExport } from "../../Types/ClientBilling";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "90%",
    marginBottom: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    "& .MuiFormLabel-root": {
      color: "black",
    },

    "& .MuiInputBase-root.Mui-disabled": {
      color: "black",
      fontWeight: "bold",
    },
  },
  cardContainer: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      width: "100%",
    },
    width: "85%",
    display: "flex",
    flexDirection: "column",
  },
}));

const csvOptions = (title: string) => {
  const filename = title.replace(/\s/g, "_");
  return mkConfig({
    filename,
    quoteStrings: true,
    showTitle: true,
    title,
    columnHeaders: [
      { key: "clientName", displayLabel: "Client Name" },
      { key: "createdFor", displayLabel: "Created For" },
      { key: "dateCreated", displayLabel: "Date Created" },
      { key: "baseBill", displayLabel: "Base Bill" },
      { key: "totalBill", displayLabel: "Total Bill" },
      { key: "candidateCount", displayLabel: "Candidate Count" },
      { key: "candidateQuota", displayLabel: "Candidate Quota" },
      { key: "candidateOverageBill", displayLabel: "Candidate Overage Bill" },
      { key: "candidateRate", displayLabel: "Candidate Rate" },
      { key: "emailCount", displayLabel: "Email Count" },
      { key: "emailQuota", displayLabel: "Email Quota" },
      { key: "emailOverageBill", displayLabel: "Email Overage Bill" },
      { key: "emailRate", displayLabel: "Email Rate" },
      { key: "textCount", displayLabel: "Text Count" },
      { key: "textQuota", displayLabel: "Text Quota" },
      { key: "textOverageBill", displayLabel: "Text Overage Bill" },
      { key: "textRate", displayLabel: "Text Rate" },
    ],
  });
};

const displayOverageCount = (count: number, quota: number) => (count - quota < 0 ? 0 : count - quota);
const displayItemTotalCost = (overageBill: number) =>
  "$" + overageBill.toLocaleString("en-US", { maximumFractionDigits: 2 });

function BillingReportsDetailed() {
  const classes = useStyles();
  const { state: report } = useLocation<ClientBillingReportDisplay>();

  const {
    baseBill,
    candidateCount,
    candidateOverageBill,
    candidateQuota,
    candidateRate,
    clientName,
    createdFor,
    emailCount,
    emailOverageBill,
    emailQuota,
    emailRate,
    textCount,
    textOverageBill,
    textQuota,
    textRate,
    totalBill,
  } = report;

  const title = `${clientName ? clientName + " " : ""}Billing Report${createdFor ? " " + createdFor : ""}`;

  const handleExportCsvData = useCallback(() => {
    const options = csvOptions(title);
    const displayReport = { ...report } as ClientBillingReportExport;
    delete displayReport.id;
    delete displayReport.clientId;
    const csv = generateCsv(options)([displayReport]);
    download(options)(csv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  if (report === undefined) {
    return <Redirect to="/billingreports" />;
  }

  const candidateOverageDisplay = displayOverageCount(candidateCount, candidateQuota);
  const candidateTotalCostDisplay = displayItemTotalCost(candidateOverageBill);
  const emailOverageDisplay = displayOverageCount(emailCount, emailQuota);
  const emailTotalCostDisplay = displayItemTotalCost(emailOverageBill);
  const textOverageDisplay = displayOverageCount(textCount, textQuota);
  const textTotalCostDisplay = displayItemTotalCost(textOverageBill);

  return (
    <div className="page-container">
      <Breadcrumb />
      <h1 className="page-title">{title}</h1>
      <div className={classes.root}>
        <Grid className={classes.cardContainer}>
          <Card>
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12}>
                  <ReportItem id="total-bill" label="Total" value={totalBill} />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <ReportItem id="base-bill" label="Base Price" value={"$" + baseBill} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="candidate-quota" label="Included Candidates" value={candidateQuota} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="candidate-count" label="Total Candidates" value={candidateCount} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem
                    id="candidate-overage"
                    label="Overage Candidates"
                    value={candidateOverageDisplay}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="candidate-rate" label="Price Per Candidate" value={"$" + candidateRate} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ReportItem
                    id="candidate-total"
                    label="Total For Candidates"
                    value={candidateTotalCostDisplay}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="email-quota" label="Included Emails" value={emailQuota} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="email-count" label="Total Emails Sent" value={emailCount} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="email-overage" label="Overage Emails" value={emailOverageDisplay} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="email-rate" label="Price Per Email" value={"$" + emailRate} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ReportItem id="email-total" label="Total For Emails" value={emailTotalCostDisplay} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="text-quota" label="Included Texts" value={textQuota} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="text-count" label="Total Texts Sent" value={textCount} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="text-overage" label="Overage Texts" value={textOverageDisplay} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <ReportItem id="text-rate" label="Price Per Text" value={"$" + textRate} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ReportItem id="text-total" label="Total For Text Messages" value={textTotalCostDisplay} />
                </Grid>
                <Grid item xs={12} sm={3} style={{ paddingTop: "15px" }}>
                  <Button
                    style={{
                      backgroundColor: "green",
                      color: "white",
                    }}
                    type="submit"
                    variant="contained"
                    onClick={handleExportCsvData}
                  >
                    Export Data
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </div>
    </div>
  );
}

function ReportItem(props: TextFieldProps) {
  return <TextField disabled variant="standard" fullWidth required {...props} />;
}

export default BillingReportsDetailed;
